import { useState, useEffect, useRef } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import MobileLayout from '../../components/MobileLayout';
import { checkVideoTask } from '../../../services/video';
import apiService from '../../../services/api';
import './MobileTaskDetail.css';

const STATUS_MAP = {
  4: { label: '已完成',  cls: 'mtd-status-badge--success' },
  3: { label: '失败',    cls: 'mtd-status-badge--failed' },
  2: { label: '生成中',  cls: 'mtd-status-badge--processing' },
  1: { label: '排队中',  cls: 'mtd-status-badge--queued' },
};

// Polling interval for in-progress tasks (ms)
const POLL_INTERVAL = 5000;

const IconDownload = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
    <polyline points="7 10 12 15 17 10"/>
    <line x1="12" y1="15" x2="12" y2="3"/>
  </svg>
);

const IconTrash = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6"/>
    <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/>
    <path d="M10 11v6M14 11v6"/>
    <path d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2"/>
  </svg>
);

const IconVideo = () => (
  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="7" width="15" height="10" rx="2"/><polyline points="17 10 22 7 22 17 17 14"/>
  </svg>
);

export default function MobileTaskDetail() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  // Use passed video state as initial data while loading
  const passedVideo = location.state?.video || null;

  const [video, setVideo]       = useState(passedVideo);
  const [loading, setLoading]   = useState(!passedVideo);
  const [error, setError]       = useState('');
  const [polling, setPolling]   = useState(false);
  const [deleting, setDeleting] = useState(false);
  const pollRef = useRef(null);

  const isInProgress = (v) => v && (v.status === 1 || v.status === 2);

  // Try to fetch task detail from the check-task endpoint
  const fetchDetail = async (silent = false) => {
    if (!id) return;
    if (!silent) setLoading(true);
    setError('');
    try {
      const data = await checkVideoTask(id);
      // checkVideoTask returns { success, status, statusText, mediaUrl } or similar
      if (data) {
        setVideo((prev) => ({
          ...prev,
          id,
          status:   data.status     ?? prev?.status,
          videoUrl: data.mediaUrl   ?? data.url ?? prev?.videoUrl ?? '',
          title:    data.title      ?? prev?.title ?? `任务 ${id}`,
          createdAt: data.createdAt ?? prev?.createdAt ?? '',
          text:     data.text       ?? prev?.text ?? '',
        }));
      }
    } catch (err) {
      if (!silent) setError(err.message || '加载失败');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    fetchDetail(!!passedVideo);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Poll while in-progress
  useEffect(() => {
    if (!video || !isInProgress(video)) {
      clearInterval(pollRef.current);
      setPolling(false);
      return;
    }

    setPolling(true);
    pollRef.current = setInterval(() => fetchDetail(true), POLL_INTERVAL);
    return () => clearInterval(pollRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [video?.status]);

  const handleDownload = () => {
    if (video?.videoUrl) {
      const a = document.createElement('a');
      a.href = video.videoUrl;
      a.download = `${video.title || 'video'}.mp4`;
      a.target = '_blank';
      a.rel = 'noreferrer';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('确认删除这个视频？删除后无法恢复。')) return;
    setDeleting(true);
    try {
      await apiService.deleteVideo(id);
      navigate(-1);
    } catch (err) {
      alert(err.message || '删除失败，请重试');
      setDeleting(false);
    }
  };

  const pill = STATUS_MAP[video?.status] || STATUS_MAP[1];
  const isCompleted = video?.status === 4;
  const isFailed    = video?.status === 3;

  return (
    <MobileLayout title="任务详情" showBack>
      <div className="mtd-root">
        {loading ? (
          <div className="mtd-loading"><div className="mtd-spinner" /></div>
        ) : error ? (
          <div className="mtd-error">{error}</div>
        ) : (
          <>
            {/* Video player / placeholder */}
            <div className="mtd-player-wrap">
              {isCompleted && video?.videoUrl ? (
                <video
                  src={video.videoUrl}
                  controls
                  playsInline
                  poster={video.thumbnail}
                  style={{ width: '100%', height: '100%' }}
                />
              ) : (
                <div className="mtd-player-placeholder">
                  <span className="mtd-player-placeholder-icon"><IconVideo /></span>
                  <span className="mtd-player-placeholder-label">
                    {isFailed ? '生成失败' : '视频生成中，请稍候...'}
                  </span>
                  {polling && (
                    <span style={{ fontSize: 12, color: '#6b7280' }}>自动刷新中</span>
                  )}
                </div>
              )}
            </div>

            {/* Status card */}
            <div className="mtd-status-card">
              <div className="mtd-status-row">
                <span className="mtd-status-label">任务状态</span>
                <span className={`mtd-status-badge ${pill.cls}`}>{pill.label}</span>
              </div>

              {video?.title && (
                <>
                  <div className="mtd-divider" />
                  <div className="mtd-info-row">
                    <span className="mtd-info-key">标题</span>
                    <span className="mtd-info-val">{video.title}</span>
                  </div>
                </>
              )}

              {video?.createdAt && (
                <>
                  <div className="mtd-divider" />
                  <div className="mtd-info-row">
                    <span className="mtd-info-key">创建时间</span>
                    <span className="mtd-info-val">{video.createdAt}</span>
                  </div>
                </>
              )}

              {video?.text && (
                <>
                  <div className="mtd-divider" />
                  <div className="mtd-info-row">
                    <span className="mtd-info-key">台词</span>
                    <span className="mtd-info-val">{video.text}</span>
                  </div>
                </>
              )}
            </div>

            {/* Polling notice */}
            {polling && (
              <div className="mtd-refresh-row">
                <span className="mtd-refresh-spin" />
                <span>视频生成中，每隔 5 秒自动刷新</span>
              </div>
            )}

            {/* Actions */}
            <div className="mtd-actions">
              {isCompleted && video?.videoUrl && (
                <button className="mtd-dl-btn" onClick={handleDownload}>
                  <IconDownload />
                  下载视频
                </button>
              )}
              <button
                className="mtd-delete-btn"
                onClick={handleDelete}
                disabled={deleting}
              >
                <IconTrash />
                {deleting ? '删除中...' : '删除视频'}
              </button>
            </div>
          </>
        )}
      </div>
    </MobileLayout>
  );
}
